import { FC, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import PermIdentityIcon from '@mui/icons-material/PermIdentity'
import { Avatar, Box, Button, CircularProgress, Grid } from '@mui/material'
import { toPng } from 'html-to-image'

import {
  useGET_studentAvatarQuery,
  useGET_studentInfoQuestionsQuery,
  usePOST_studentAvatarMutation,
  usePOST_studentInfoMutation,
} from 'redux/api/studentsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import Tabs from 'components/organisms/tabs/Tabs'
import AvatarStepper from 'components/utilized/student/AvatarStepper'
import { AvatarTab } from 'components/utilized/student/AvatarTab'
import StudentQuestionList from 'components/utilized/student/StudentQuestionList'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import formModel, { initialValues, validationSchema } from 'utils/formModels/avatarModel'

import { PRIMARY_TRANSPARENT_GREEN } from '../../../utils/customizedTheme'
import { useMedia } from 'utils/customHooks/useMedia'
import { setOpenAvatar } from 'redux/ui/dashboardUI'
import { AppDispatch } from 'redux/store'
import { useDispatch } from 'react-redux'

const AvatarForm: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const ref = useRef<HTMLDivElement>(null)
  const [dataUrl, setDataUrl] = useState(null)
  const { answers } = formModel()

  const { isLoading: entityLoading, data: entity, error: entityError } = useGET_studentAvatarQuery()
  const {
    isLoading: questionsLoading,
    data: questionsData,
    error: questionsError,
  } = useGET_studentInfoQuestionsQuery()

  const [
    addEntity,
    { isLoading: createLoading, isSuccess: createSuccess, error: createError, data: addResponseData },
  ] = usePOST_studentInfoMutation()
  const [
    addAvatar,
    { isLoading: createAvatarLoading, isSuccess: createAvatarSuccess, error: createAvatarError, data: addAvatarResponseData },
  ] = usePOST_studentAvatarMutation()
  const [activeStep, setActiveStep] = useState(0)
  const isLoading = createLoading || entityLoading || questionsLoading || createAvatarLoading
  const error = createError || entityError || questionsError || createAvatarError

  useErrorAlert(error)
  useSuccessAlert(createSuccess ? t('alerts.message-sent') : '')

  const { isUnder900px, isUnder1536px } = useMedia()
  
  const answersValues = questionsData?.results.map((question) => {
    return {
      question_id: question.id,
      answer_text:  question.answer_text ? question.answer_text : '',
      question: question.question_text,
    }
  }) ?? []

  const serverValues = entity && answersValues
    ? {
        face: { id: entity?.avatar_parts.face_id, url: entity.avatar_parts.face_url },
        eyes: { id: entity?.avatar_parts.eyes_id, url: entity.avatar_parts.eyes_url },
        brows: { id: entity?.avatar_parts.brows_id, url: entity.avatar_parts.brows_url },
        nose: { id: entity?.avatar_parts.nose_id, url: entity.avatar_parts.nose_url },
        lips: { id: entity?.avatar_parts.lips_id, url: entity.avatar_parts.lips_url },
        answers: answersValues
      }
    : null

  const onSaveClick = useCallback(() => {
    if (ref.current === null) {
      return
    }
    toPng(ref.current, { includeQueryParams: false })
      .then((dataUrl) => {
        setDataUrl(dataUrl)
        setActiveStep(1)
      })
      .catch((err) => {
        //console.log(err)
      })
  }, [ref])

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={serverValues || initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: any) => {
          const studentInfoValues = values.answers.map((answer) => ({
            question_id: answer.question_id,
            answer_text: answer.answer_text,
          }))
          const formData = new FormData()
          formData.append('avatar', dataUrl)
          formData.append('face_id', values.face.id)
          formData.append('eyes_id', values.eyes.id)
          formData.append('brows_id', values.brows.id)
          formData.append('nose_id', values.nose.id)
          formData.append('lips_id', values.lips.id)
          addAvatar(formData)
          addEntity(studentInfoValues)
          setActiveStep(2)
          dispatch(setOpenAvatar(false))
        }}
      >
        {({ values, setFieldValue, resetForm }) => {
          return (
            <Form
              style={{
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Grid
                container
                spacing={2}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: '1',
                  width: '100%',
                }}
              >
                <>
                  {/* Avatar */}
                  <Grid item xs={12} md={3} sx={{ textAlign: isUnder900px ? 'center' : 'right' }}>
                    {' '}
                    {values?.face.url !== null ? (
                      <>
                        <Box className='avatar' ref={ref} sx={{ display: 'inline-block' }}>
                          <Box
                            className='face'
                            sx={{
                              backgroundImage: `url(${values?.face.url})`,
                            }}
                          />
                          <Box className='eye-brow eye-brow-left'>
                            <Box
                              className='brow'
                              sx={{
                                backgroundImage: `url(${values?.brows.url})`,
                              }}
                            />
                            <Box
                              className='eye'
                              sx={{
                                backgroundImage: `url(${values?.eyes.url})`,
                              }}
                            />
                          </Box>
                          <Box className='eye-brow eye-brow-right'>
                            <Box
                              className='brow'
                              sx={{
                                backgroundImage: `url(${values?.brows.url})`,
                              }}
                            />
                            <Box
                              className='eye'
                              sx={{
                                backgroundImage: `url(${values?.eyes.url})`,
                              }}
                            />
                          </Box>
                          <Box
                            className='nose'
                            sx={{
                              backgroundImage: `url(${values?.nose.url})`,
                            }}
                          />
                          <Box
                            className='lips'
                            sx={{
                              backgroundImage: `url(${values?.lips.url})`,
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Avatar
                        sx={{
                          bgcolor: PRIMARY_TRANSPARENT_GREEN,
                          width: '200px',
                          height: '200px',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                        }}
                        variant='circular'
                      >
                        <PermIdentityIcon sx={{ width: '4rem', height: '4rem' }} />
                      </Avatar>
                    )}
                  </Grid>
                  {/* Type tabs */}
                  <Grid item xs={12} md={9} className='avatar-tabs'>
                    {!dataUrl ? (
                      <>
                        <Tabs tabKey='face'>
                          <AvatarTab
                            tabText={t('student.face')}
                            tabKey='face'
                            onClick={(value) => setFieldValue('face', { id: value.id, url: value.file })}
                          />
                          <AvatarTab
                            disabled={values?.face.url === null}
                            tabText={t('student.eyes')}
                            tabKey='eyes'
                            onClick={(value) => setFieldValue('eyes', { id: value.id, url: value.file })}
                          />
                          <AvatarTab
                            disabled={!values?.face.url}
                            tabText={t('student.brows')}
                            tabKey='brows'
                            onClick={(value) => setFieldValue('brows', { id: value.id, url: value.file })}
                          />
                          <AvatarTab
                            disabled={!values?.face.url}
                            tabText={t('student.nose')}
                            tabKey='nose'
                            onClick={(value) => setFieldValue('nose', { id: value.id, url: value.file })}
                          />
                          <AvatarTab
                            disabled={!values?.face.url}
                            tabText={t('student.smile')}
                            tabKey='lips'
                            onClick={(value) => setFieldValue('lips', { id: value.id, url: value.file })}
                          />
                        </Tabs>

                        <Box sx={{ marginLeft: 'auto', textAlign: 'end' }}>
                          <Button
                            variant='contained'
                            color={'success'}
                            sx={{ fontWeight: '500', minHeight: '2.5rem', margin: '1rem' }}
                            onClick={onSaveClick}
                            disabled={isLoading}
                            disableElevation
                          >
                            {isLoading && dataUrl ? (
                              <CircularProgress variant='indeterminate' size={20} sx={{ color: '#000' }} />
                            ) : (
                              <>{`${t('common.next')}`}</>
                            )}
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <StudentQuestionList fieldProps={{ ...answers }} />
                        <Box sx={{ display: 'flex' }}>
                          <Button
                            variant='contained'
                            color={'success'}
                            sx={{ fontWeight: '500', minHeight: '2.5rem', marginTop: '1rem' }}
                            onClick={() => {
                              setActiveStep(0)
                              setDataUrl(null)
                            }}
                            disabled={isLoading}
                            disableElevation
                          >
                            {isLoading ? (
                              <CircularProgress variant='indeterminate' size={20} sx={{ color: '#000' }} />
                            ) : (
                              <>{`${t('common.back')}`}</>
                            )}
                          </Button>
                          <SubmitButton
                            text={`${t('common.save')}`}
                            sx={{ fontWeight: '500', minHeight: '2.5rem', marginTop: '1rem' }}
                            isLoading={isLoading}
                          />
                        </Box>
                      </>
                    )}
                  </Grid>
                </>
              </Grid>
              
              { !isUnder1536px &&
              <Box sx={{ maxWidth: 600, height: '100%', position: 'absolute', left: 10 }}>
                <AvatarStepper activeStep={activeStep} />
              </Box>
              }
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default AvatarForm
